/**
 * @file 编辑报名任务全屏弹窗
 */

import { Button, State, Toast } from '@seiue/ui'
import { pick } from '@seiue/util'
import { Form } from 'antd'
import moment from 'moment'
import { FC, useEffect, useRef, useState } from 'react'

import {
  OptionReq,
  TaskSetting,
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  enrollApi,
} from 'packages/sdks-next/growp'

import { CommonBasicInfoRef, SubmitValues } from '@/components/CommonBasicInfo'
import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'

import { EditTaskModalProps } from '../types'

import { BasicInfo } from './BasicInfo'

/**
 * 编辑报名任务弹窗
 *
 * @param param0 - 参数
 * @param param0.onClose - 关闭弹窗
 * @param param0.taskId - 任务 ID
 * @param param0.activityId - 活动 ID
 * @param param0.activityTaskOptions - 活动任务选项
 * @returns 组件
 */
export const EditEnrollTaskModal: FC<EditTaskModalProps> = ({
  onClose,
  activityId,
  taskId,
  activityTaskOptions,
}) => {
  const isEdit = !!taskId

  const [form] = Form.useForm()
  const [title, setTitle] = useState<string>('')
  const [commonBasicInfo, setCommonBasicInfo] = useState<SubmitValues>({})
  const basicInfoRef = useRef<CommonBasicInfoRef>(null)

  const { data: editTask, loading } = enrollApi.useLoadEnrollActivityTask(
    {
      id: taskId || 0,
      query: {
        isForeground: false,
      },
    },
    { disable: !taskId }
  )

  const filterTaskOptions =
    activityTaskOptions?.filter((o) => o.value !== taskId) || []

  const { api: createActivityTask } = enrollApi.useCreateEnrollActivityTask({
    reload: [
      activityApi.queryTasks,
      activityApi.loadActivity,
      enrollApi.loadEnrollActivityTask,
    ],
  })

  const { api: updateActivityTask } = enrollApi.useUpdateEnrollActivityTask({
    reload: [
      activityApi.queryTasks,
      activityApi.loadActivity,
      enrollApi.loadEnrollActivityTask,
    ],
  })

  useEffect(() => {
    form.resetFields()

    const settings = editTask?.task?.setting || {}

    form.setFieldsValue({
      ...editTask?.task,
      settings,
      options:
        editTask?.options === undefined
          ? undefined
          : editTask?.options.sort((a, b) => (b.weight || 0) - (a.weight || 0)),
    })
    setTitle(editTask?.task?.name || '')

    setCommonBasicInfo({
      ...pick({ ...editTask?.task }, [
        'endAt',
        'adminIds',
        'timedStartAt',
        'prevTaskIds',
        'setting',
      ]),
      startAt: editTask?.task
        ? editTask.task.startAt
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      isShow:
        editTask?.task?.isShow !== undefined ? editTask.task.isShow : true,
    })
  }, [editTask, form])

  if (loading) return <State.Loading />
  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || '未命名报名'}
          onClose={onClose}
        />
      }
      headerRight={
        <>
          <Button
            type="primary"
            onClick={async () => {
              form.submit()
              const commonRefInfo = basicInfoRef.current?.getBasicInfo()

              form.validateFields().then(async (values) => {
                const settings = values.settings as TaskSetting
                const data = {
                  task: {
                    ...pick(values, ['name', 'description', 'isShow']),
                    ...commonRefInfo,
                    activityId,
                    type: TaskTypeEnum.Enroll,
                    status: TaskStatusEnum.Published,
                    setting: {
                      maxSelectLimit: settings.maxSelectLimit ?? 0,
                      minSelectLimit: settings.minSelectLimit ?? 0,
                      allowEdit: settings.allowEdit ?? false,
                    },
                  },
                  options: [...(values.options as OptionReq[])],
                }
                data.options = data.options.reverse().map((option, key) => {
                  option.weight = key
                  return option
                })
                if (isEdit) {
                  await updateActivityTask({
                    id: taskId,
                    payload: data,
                  })
                } else {
                  await createActivityTask(data)
                }

                Toast.success(isEdit ? '更新任务成功' : '保存任务成功')

                onClose()
              })
            }}
          >
            {isEdit ? '更新' : '确认'}
          </Button>
        </>
      }
      content={
        <ContentCard
          style={{ minHeight: 200 }}
          contentStyle={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px 32px',
          }}
        >
          <BasicInfo
            setTitle={setTitle}
            form={form}
            commonBasicInfo={commonBasicInfo}
            basicInfoRef={basicInfoRef}
            activityTaskOptions={filterTaskOptions}
            initOptions={editTask?.options || []}
            openTypeDisabled={editTask?.isSubmit}
          />
        </ContentCard>
      }
    />
  )
}
